import { ActionReducerMap, createFeatureSelector, createSelector } from '@ngrx/store';
import {
  find as _find,
  flatMap as _flatMap,
  flatten as _flatten,
  get as _get,
  uniq as _uniq
} from 'lodash';

import { addVehicleInfoQuery, addVehicleQuoteQuery } from '@amfam/policy/add-vehicle/data-access';
import {
  PolicySelectors as policyQuery,
  PolicySummarySelectors as policySummaryQuery
} from '@amfam/policy/data-access';
import { RiskModel } from '@amfam/policy/models';
import { PaymentScheduleStatusType } from '@amfam/shared/models';
import { agentQuery } from '@amfam/shared/utility/agent';

import * as fromAddVehicle from './add-vehicle-feature.reducer';

export const selectVehicleRisks = createSelector(
  policyQuery.selectAutoPolicyRisks,
  policySummaryQuery.selectPolicySummaries,
  (risks, policySummaries) => {
    const rpvRisks: Array<RiskModel> = new Array<RiskModel>();
    risks.forEach(risk => {
      policySummaries.forEach(policy => {
        if (_get(risk, 'policyNumber', '') === _get(policy, 'policyNumber', '')) {
          risk = Object.assign({}, risk, { contractState: policy.contractState });
          rpvRisks.push(risk);
        }
      });
    });
    return rpvRisks;
  }
);

export const selectAdvancedAutoPolicyRisks = createSelector(
  policyQuery.selectAdvanceAutoPolicies,
  autoPolicies =>
    _flatten(
      autoPolicies.map(policy => policy.vehicles.map(vehicle => new RiskModel(vehicle, policy)))
    )
);

export const selectVehicles = createSelector(policyQuery.selectAdvanceAutoPolicies, autoPolicies =>
  _flatMap(autoPolicies.map(policy => policy.vehicles))
);

export const selectAdvancedAutoPolicyGarageLocation = createSelector(
  selectAdvancedAutoPolicyRisks,
  autoPoliciesRisks =>
    // AS: Assumption here is that the garage address of multiple advanced autopolicies
    // cannot be different.
    _uniq(autoPoliciesRisks.map(risk => risk.address.getStringifiedAddress()))
);

/**
 * Active Agent selectors
 */
export const selectAllActiveAgents = createSelector(
  agentQuery.getRealAgents,
  policyQuery.selectPolicies,
  (agents, policies) =>
    agents.filter(
      agent => agent && _find(policies, p => p.assignedProducer.producerIdNum === agent.id)
    )
);

// MR: List of agents that specifically produced the Advance Auto Policies
export const selectAdvanceAutoPolicyProducingAgents = createSelector(
  selectAllActiveAgents,
  policyQuery.selectAdvanceAutoPolicies,
  (agents, policies) =>
    agents.filter(agent =>
      policies.some(policy => policy.assignedProducer.producerIdNum === agent.id)
    )
);

// MR: List of agents that specifically produced the Classic Policies
export const selectClassicAutoPolicyProducingAgents = createSelector(
  selectAllActiveAgents,
  policyQuery.selectClassicPolicies,
  (agents, policies) =>
    agents.filter(agent =>
      policies.some(policy => policy.assignedProducer.producerIdNum === agent.id)
    )
);

export interface State {
  users: fromAddVehicle.State;
}

export const reducers: ActionReducerMap<State> = {
  users: fromAddVehicle.reducer
};

export const selectVehicleInfoState = createFeatureSelector<fromAddVehicle.State>(
  fromAddVehicle.ADD_VEHICLE_FEATURE_KEY
);

export const selectVinIds = createSelector(selectVehicleInfoState, fromAddVehicle.selectVinIds);
export const selectVehicleInfoEntities = createSelector(
  selectVehicleInfoState,
  fromAddVehicle.selectvehicleInfoEntities
);

export const selectCurrentVehicleInfo = createSelector(selectVehicleInfoEntities, userEntities => {
  if (Object.values(userEntities)) {
    return Object.values(userEntities)[0];
  }
});

export const selectCurrentVehicleVinInfo = createSelector(
  selectVehicleInfoEntities,
  userEntities => {
    if (Object.values(userEntities)) {
      return _get(Object.values(userEntities)[0], 'primaryVehicleInfo.vin');
    }
  }
);

export const selectCurrentVehicleMappedFromInfo = createSelector(
  selectVehicleInfoEntities,
  addVehicleInfoQuery.selectCoveragesModelledAfter,
  (userEntities, vehiclesInPolicy) => {
    if (Object.values(userEntities) && vehiclesInPolicy) {
      return vehiclesInPolicy;
    }
  }
);

export const selectCurrentVehiclePrimaryDrivers = createSelector(
  selectVehicleInfoEntities,
  userEntities =>
    _flatMap(
      Object.values(userEntities)[0].driverAssignment.filter(driver => driver.primary),
      driver => driver.name
    )
);

export const selectVehicleDriverAssignmentList = createSelector(
  selectVehicleInfoEntities,
  userEntities => {
    if (Object.values(userEntities)[0]) {
      return Object.values(userEntities)[0].driverAssignment;
    }
  }
);

export const selectCurrentAutoPolicy = createSelector(
  selectVehicleRisks,
  selectCurrentVehicleInfo,
  policyQuery.selectAdvanceAutoPolicies,
  (autoRisks, vehicleInfoState, autoPolicies) => {
    const matchedAutoRisk = autoRisks.find(
      autoRisk =>
        autoRisk.address.getStringifiedAddress() === _get(vehicleInfoState, 'garageAddress')
    );

    return autoPolicies.find(
      autoPolicy => autoPolicy.policyNumber === _get(matchedAutoRisk, 'policyNumber', '')
    );
  }
);

export const selectVehicleTypeOfAgentEmailNotification = createSelector(
  selectVehicleInfoEntities,
  userEntities => {
    if (Object.values(userEntities)[0]) {
      return Object.values(userEntities)[0].typeOfAgentEmail;
    }
  }
);

export const selectVehicleTypeOfPartnerEmailNotification = createSelector(
  selectVehicleInfoEntities,
  userEntities => {
    if (Object.values(userEntities)[0]) {
      return Object.values(userEntities)[0].typeOfAgentEmail;
    }
  }
);

export const selectAddVehicleExitFlowStatus = createSelector(
  selectVehicleInfoEntities,
  userEntities => {
    if (Object.values(userEntities)[0]) {
      return Object.values(userEntities)[0].exitFlow;
    }
  }
);

export const selectCoverages = createSelector(
  selectVehicleInfoState,
  state => state.coverages || []
);
export const selectFuturePayments = createSelector(
  addVehicleQuoteQuery.selectFuturePayments,
  payments =>
    payments.filter(p => p?.estPaymentAmount > 0 && p.status === PaymentScheduleStatusType.PLANNED)
);

export const selectRemainingVehicles = createSelector(
  policyQuery.selectAutoPolicyRisks,
  addVehicleQuoteQuery.selectNewVehiclePolicyNumber,
  (risks, policyNumber) =>
    risks
      .filter(r => r.policyNumber === policyNumber)
      .map(risk => `${risk.year || ''} ${risk.make || ''} ${risk.model || ''}`)
);

export const selectShouldLoadFuturePayments = createSelector(
  selectCurrentVehicleInfo,
  state => !!state.navigateToFuturePaymentSchedule
);

export const selectCanAddVehicle = createSelector(selectVinIds, ids => ids && ids.length > 0);
export const selectAddVehicleOtherExitFlowStatus = createSelector(
  selectVehicleInfoEntities,
  userEntities => {
    if (Object.values(userEntities)[0]) {
      return Object.values(userEntities)[0].otherExitFlow;
    }
  }
);

export const addVehicleFeatureQuery = {
  selectCurrentVehicleInfo,
  selectCurrentVehicleVinInfo,
  selectVehicleInfoEntities,
  selectVinIds,
  selectVehicleInfoState,
  selectVehicleRisks,
  selectAllActiveAgents,
  selectAdvancedAutoPolicyRisks,
  selectVehicles,
  selectCurrentVehiclePrimaryDrivers,
  selectVehicleDriverAssignmentList,
  selectCurrentAutoPolicy,
  selectCurrentVehicleMappedFromInfo,
  selectVehicleTypeOfAgentEmailNotification,
  selectAdvanceAutoPolicyProducingAgents,
  selectAddVehicleExitFlowStatus,
  selectCoverages,
  selectFuturePayments,
  selectRemainingVehicles,
  selectShouldLoadFuturePayments,
  selectCanAddVehicle,
  selectClassicAutoPolicyProducingAgents,
  selectAddVehicleOtherExitFlowStatus,
  selectAdvancedAutoPolicyGarageLocation,
  selectVehicleTypeOfPartnerEmailNotification,
  addVehicleQuoteQuery
};
